/**
 * Coalesces high-frequency input events into a single callback per animation frame.
 *
 * Mouse/wheel events can fire much faster than the display refresh rate.
 * Tools accumulate deltas externally, call `schedule()` on each event, and the
 * provided `onFlush` callback runs at most once per frame with the accumulated state.
 *
 * Usage:
 *   1. Accumulate deltas in the tool's handleEvent
 *   2. Call `schedule()` — queues a rAF if one isn't already pending
 *   3. `onFlush` fires once per frame — read & reset the accumulated deltas there
 *   4. Call `flush()` on drag_end to apply remaining deltas synchronously
 *   5. Call `cancel()` on deactivate/cancel to discard pending work
 */
export class FrameCoalescer {
    private rafHandle: number | null = null;

    constructor(private readonly onFlush: () => void) {}

    /** Schedule a flush on the next animation frame (no-op if already scheduled). */
    schedule(): void {
        if (this.rafHandle === null) {
            this.rafHandle = requestAnimationFrame(() => {
                this.rafHandle = null;
                this.onFlush();
            });
        }
    }

    /** Apply accumulated state immediately (cancels any pending frame). */
    flush(): void {
        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
        this.onFlush();
    }

    /** Discard pending frame without applying. */
    cancel(): void {
        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
    }
}
